import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  ArrowLeft,
  CheckCheck,
  ListFilter,
  RefreshCw,
  Search,
  Tag,
  Telescope,
  SlidersHorizontal,
} from 'lucide-react';
import { Link, Prompt } from 'react-router-dom';
import { AnomalyEvent, AnomalyLabel, AnomalyOverview, AnomalyScope, LabelAction, SaveReview, SetScanControl } from './types';
import { dateTime, filterEvents, initialFilters, resolveLabel } from './helpers';
import EventChart from './Chart';
import EventEditor from './Editor';
import Labels from './Labels';
import ScanControls from './ScanControls';
import BehaviourPanel, { BehaviourService } from './BehaviourPanel';
import './anomalies.css';

type Props = {
  scope?: AnomalyScope;
  events: AnomalyEvent[];
  labels: AnomalyLabel[];
  scan?: AnomalyOverview['scan'];
  loading: boolean;
  error: string | null;
  onSave: (command: SaveReview) => Promise<AnomalyEvent>;
  onLabelAction: (command: LabelAction) => Promise<void>;
  onRefresh: () => unknown;
  onScanControl?: (command: SetScanControl) => Promise<void>;
  behaviourService?: BehaviourService;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => unknown;
};
const statuses = [
  { id: 'pending', title: 'Til vurdering' },
  { id: 'investigating', title: 'Undersøges' },
  { id: 'reviewed', title: 'Vurderet' },
];
export default function AnomaliesPage(props: Props) {
  const { events, labels, scan, onSave } = props;
  const defaultFilters = { ...initialFilters, status: props.scope ? 'all' : initialFilters.status };
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showLabels, setShowLabels] = useState(false);
  const [showBehaviour, setShowBehaviour] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const dirty = useRef(false);
  const activeId = useRef<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);
  const onDirtyChange = useCallback((value: boolean) => {
    dirty.current = value;
    setHasDraft(value);
    if (value) setSelectedId(activeId.current);
  }, []);
  const filtered = useMemo(() => filterEvents(events, labels, filters), [events, labels, filters]);
  const selected =
    (hasDraft ? events.find((event) => event.id === selectedId) : undefined) ||
    filtered.find((event) => event.id === selectedId) ||
    filtered[0];
  activeId.current = selected?.id || null;
  const counts = statuses.map(
    (status) => events.filter((e) => e.review.status === status.id).length
  );
  function mayLeave() {
    return (
      !busy &&
      (!dirty.current ||
        window.confirm('Du har en annotation, der ikke er gemt. Vil du forlade den?'))
    );
  }
  function changeFilters(next: Partial<typeof filters>) {
    if (mayLeave()) {
      dirty.current = false;
      setHasDraft(false);
      setSelectedId(null);
      setFilters((current) => ({ ...current, ...next }));
    }
  }
  function saved(next: boolean) {
    dirty.current = false;
    setHasDraft(false);
    setMessage('Annotation gemt.');
    if (next) {
      const index = filtered.findIndex((e) => e.id === selected?.id);
      setSelectedId(
        filtered[index + 1]?.id || filtered.find((e) => e.id !== selected?.id)?.id || null
      );
    }
  }
  const scanFailed =
    scan?.lastFailedAt && (!scan.lastCompletedAt || scan.lastFailedAt > scan.lastCompletedAt);
  return (
    <main className="anomalies-page">
      <Prompt
        when={hasDraft || busy}
        message="Du har en annotation, der ikke er gemt. Vil du forlade siden?"
      />
      <div className="anomaly-topline">
        <Link to={props.scope ? '/sensors' : '/monitoring'}>
          <ArrowLeft size={15} /> {props.scope ? 'Enhedsoversigt' : 'Monitoring'}
        </Link>
        <span>FLOW & GSM</span>
      </div>
      <header className="anomaly-heading">
        <div>
          <h1>
            <Activity size={28} /> Afvigelser
          </h1>
          <p>Se hvad der ændrede sig. Tilføj en forklaring. Videre.</p>
        </div>
        <div className="anomaly-heading-actions">
          {props.behaviourService && <button className="anomaly-button" disabled={busy} onClick={() => setShowBehaviour(true)}><SlidersHorizontal size={16} /> Normal adfærd & statistik</button>}
          <button className="anomaly-button" disabled={busy} onClick={() => setShowLabels(true)}>
            <Tag size={16} /> Labels
          </button>
          <button
            className="anomaly-button"
            disabled={busy || props.loading}
            onClick={() => props.onRefresh()}
            aria-label="Opdatér afvigelser"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </header>
      {props.scope && (
        <div className="anomaly-warning" aria-label="Valgt sensor">
          <strong>Historik for {events[0]?.sensorTitle || props.scope.sensorId}</strong>
          {' · '}{events[0]?.orgTitle || props.scope.orgId}
          <p>Viser kun denne sensors afvigelser. Alle vurderingsstatusser er med fra start.</p>
          <Link to="/monitoring/anomalies">Vis afvigelser for alle enheder</Link>
        </div>
      )}
      {props.onScanControl && (
        <ScanControls
          scan={scan}
          disabled={props.loading || Boolean(props.error)}
          onChange={props.onScanControl}
        />
      )}
      <div className="anomaly-summary" aria-label="Overblik">
        {statuses.map((status, i) => (
          <button
            key={status.id}
            className={filters.status === status.id ? 'active' : ''}
            aria-pressed={filters.status === status.id}
            onClick={() => changeFilters({ status: status.id })}
          >
            <span className={`anomaly-status-dot ${status.id}`} />
            <span>{status.title}</span>
            <strong>
              {counts[i]}
              {props.hasMore ? '+' : ''}
            </strong>
          </button>
        ))}
        <div className="anomaly-scan-status">
          {scan?.lastCompletedAt ? (
            <>
              Sidst analyseret
              <br />
              <strong>{dateTime(scan.lastCompletedAt)}</strong>
            </>
          ) : props.loading ? (
            'Indlæser…'
          ) : (
            'Afventer første analyse'
          )}
        </div>
      </div>
      {props.error && (
        <div role="alert" className="anomaly-error">
          Kunne ikke opdatere oversigten. {props.error}{' '}
          <button onClick={() => props.onRefresh()}>Prøv igen</button>
        </div>
      )}
      {scanFailed && (
        <div role="alert" className="anomaly-warning">
          Seneste analyse kunne ikke gennemføres. Listen viser tidligere fund.
        </div>
      )}
      <div className="anomaly-filters">
        <select
          aria-label="Vurderingsstatus"
          value={filters.status}
          onChange={(e) => changeFilters({ status: e.target.value })}
        >
          <option value="all">Alle statusser</option>
          {statuses.map((status) => <option key={status.id} value={status.id}>{status.title}</option>)}
        </select>
        <div className="anomaly-search">
          <Search size={17} />
          <input
            aria-label="Søg afvigelser"
            placeholder="Søg anlæg, kunde eller forklaring…"
            value={filters.search}
            onChange={(e) => changeFilters({ search: e.target.value })}
          />
        </div>
        <select
          aria-label="Anlægstype"
          value={filters.family}
          onChange={(e) => changeFilters({ family: e.target.value })}
        >
          <option value="all">Alle anlæg</option>
          <option value="FLOW">Flow / niveau</option>
          <option value="GSM">GSM anlæg</option>
        </select>
        <select
          aria-label="Periode"
          value={filters.days}
          onChange={(e) => changeFilters({ days: e.target.value })}
        >
          <option value="all">Alle datoer</option>
          <option value="7">Seneste 7 dage</option>
          <option value="30">Seneste 30 dage</option>
          <option value="90">Seneste 90 dage</option>
        </select>
        <select
          aria-label="Filtrér efter label"
          value={filters.labelId}
          onChange={(e) => changeFilters({ labelId: e.target.value })}
        >
          <option value="all">Alle labels</option>
          {labels
            .filter((l) => !l.mergedInto)
            .map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
                {l.archived ? ' (arkiveret)' : ''}
              </option>
            ))}
        </select>
        <button
          className="anomaly-reset"
          onClick={() => changeFilters(defaultFilters)}
          aria-label="Nulstil filtre"
        >
          <ListFilter size={17} />
        </button>
      </div>
      <div className="anomaly-list-caption">
        <span>
          {filtered.length} hændelser{props.hasMore ? ' blandt de indlæste' : ''} · nyeste først
        </span>
        <span role="status">{message}</span>
      </div>
      {props.loading ? (
        <div className="anomaly-empty" role="status">
          <Activity size={30} />
          <h2>Henter afvigelser…</h2>
        </div>
      ) : !filtered.length && !selected ? (
        <div className="anomaly-empty">
          {events.length ? <CheckCheck size={36} /> : <Telescope size={36} />}
          <h2>
            {events.length
              ? 'Ingen hændelser i denne visning'
              : scan?.lastCompletedAt
              ? 'Ingen afvigelser fundet endnu'
              : 'Klar til de første afvigelser'}
          </h2>
          <p>
            {events.length
              ? 'Skift filtre, eller se de vurderede hændelser.'
              : 'Niveauændringer og usædvanligt GSM-forbrug vises her efter den automatiske analyse. Labels opretter du, når du vurderer en hændelse.'}
          </p>
          {events.length > 0 && (
            <button
              className="anomaly-button"
              onClick={() => changeFilters({ ...initialFilters, status: 'all' })}
            >
              Vis alle indlæste hændelser
            </button>
          )}
        </div>
      ) : (
        <div className="anomaly-workspace">
          <section className="anomaly-queue" aria-label="Hændelser">
            <div className="anomaly-queue-items">
              {filtered.map((event) => {
                const label = resolveLabel(event.review.labelId, labels);
                return (
                  <button
                    key={event.id}
                    className={`anomaly-event ${selected?.id === event.id ? 'selected' : ''}`}
                    aria-pressed={selected?.id === event.id}
                    disabled={busy}
                    onClick={() => {
                      if (selected?.id !== event.id && mayLeave()) {
                        dirty.current = false;
                        setHasDraft(false);
                        setSelectedId(event.id);
                        setMessage('');
                      }
                    }}
                  >
                    <span className="anomaly-event-top">
                      <span className="anomaly-family">
                        {event.deviceFamily === 'FLOW' ? 'Flow / niveau' : 'GSM'}
                      </span>
                      <time>{dateTime(event.occurredAt)}</time>
                    </span>
                    <strong>{event.sensorTitle}</strong>
                    <span className="anomaly-event-org">{event.orgTitle}</span>
                    <span className="anomaly-event-title">{event.title}</span>
                    <EventChart event={event} small />
                    <span className={`anomaly-event-label ${event.review.status}`}>
                      {label ? (
                        <>
                          <Tag size={12} />
                          {label.name}
                        </>
                      ) : event.review.status === 'investigating' ? (
                        'Undersøges'
                      ) : (
                        'Mangler forklaring'
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
          {selected && (
            <EventEditor
              key={selected.id}
              event={selected}
              busy={busy}
              labels={labels}
              onSave={onSave}
              onSaved={saved}
              onDirtyChange={onDirtyChange}
              onBusyChange={setBusy}
            />
          )}
        </div>
      )}
      {props.hasMore && (
        <div className="anomaly-load-more">
          <p>
            Oversigten og filtrene omfatter de indlæste hændelser. Hent flere for at se ældre fund.
          </p>
          <button
            className="anomaly-button"
            disabled={props.loadingMore || busy}
            onClick={() => props.onLoadMore()}
          >
            {props.loadingMore ? 'Henter…' : 'Hent flere hændelser'}
          </button>
        </div>
      )}
      <Labels
        labels={labels}
        open={showLabels}
        onClose={() => setShowLabels(false)}
        onAction={props.onLabelAction}
      />
      {props.behaviourService && <BehaviourPanel open={showBehaviour} onClose={() => setShowBehaviour(false)} service={props.behaviourService} initialSensorId={selected?.sensorId} />}
    </main>
  );
}
